import React, { useEffect, useRef, useState } from 'react';
import { ImagePlus, Mic, MicOff, Send, Square, Volume2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { useRiqueSpeech } from '@/hooks/useRiqueSpeech';
import { transcribeRiqueAudio } from '@/lib/riqueApi';
import { toast } from 'react-hot-toast';

interface RiqueComposerProps {
  disabled?: boolean;
  busy?: boolean;
  onSend: (text: string) => Promise<void>;
  onStop?: () => void;
  onPickImage?: (file: File) => Promise<void>;
  lastAssistantText?: string;
  compact?: boolean;
  suggestions?: string[];
  onOpenFaq?: () => void;
  faqTotal?: number;
  /** Empty state: suggestions rendered by message list; composer stays clean */
  emptyState?: boolean;
  autoFocus?: boolean;
}

export const RiqueComposer: React.FC<RiqueComposerProps> = ({
  disabled,
  busy = false,
  onSend,
  onStop,
  onPickImage,
  lastAssistantText,
  compact = false,
  suggestions = [],
  onOpenFaq,
  faqTotal,
  emptyState = false,
  autoFocus = false,
}) => {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const [whisperRecording, setWhisperRecording] = useState(false);

  const handleTranscript = React.useCallback((transcript: string) => {
    setText((prev) => (prev ? `${prev} ${transcript}` : transcript));
  }, []);

  const { supported, isRecording, interim, start, stop, speak } = useRiqueSpeech(handleTranscript);

  useEffect(() => {
    if (autoFocus) textareaRef.current?.focus();
  }, [autoFocus]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = '0px';
    const next = Math.min(el.scrollHeight, 160);
    el.style.height = `${Math.max(next, compact ? 44 : 52)}px`;
  }, [text, compact]);

  useEffect(() => {
    if (!busy || !onStop) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onStop();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onStop]);

  const sendText = async (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed || sending || disabled || busy) return;
    setSending(true);
    try {
      await onSend(trimmed);
      setText('');
    } finally {
      setSending(false);
      requestAnimationFrame(() => textareaRef.current?.focus());
    }
  };

  const handleSend = async () => {
    await sendText(text);
  };

  const startWhisperRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        if (blob.size < 500) {
          toast.error('Áudio muito curto.');
          return;
        }
        try {
          const transcricao = await transcribeRiqueAudio(blob);
          if (transcricao) setText((prev) => (prev ? `${prev} ${transcricao}` : transcricao));
          else toast.error('Transcrição vazia. Digite a mensagem ou tente novamente.');
        } catch (e: unknown) {
          toast.error(e instanceof Error ? e.message : 'Erro na transcrição Whisper');
        }
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setWhisperRecording(true);
    } catch {
      toast.error('Permissão de microfone negada.');
    }
  };

  const stopWhisperRecording = () => {
    mediaRecorderRef.current?.stop();
    setWhisperRecording(false);
  };

  const toggleMic = () => {
    if (whisperRecording) {
      stopWhisperRecording();
      return;
    }
    if (isRecording) {
      stop();
      return;
    }
    if (supported) start();
    else startWhisperRecording();
  };

  const showInlineSuggestions = !emptyState && suggestions.length > 0;

  return (
    <div className={cn('shrink-0 bg-gradient-to-t from-background via-background to-transparent', compact ? 'pt-2' : 'pt-3')}>
      {showInlineSuggestions ? (
        <div className="mb-2 flex gap-1.5 overflow-x-auto pb-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {suggestions.slice(0, 3).map((s) => (
            <button
              key={s}
              type="button"
              disabled={disabled || sending || busy}
              className="shrink-0 rounded-full border border-border/70 bg-background px-3 py-1 text-xs text-muted-foreground transition-colors hover:border-border hover:bg-muted/50 hover:text-foreground disabled:opacity-50"
              onClick={() => void sendText(s)}
            >
              {s}
            </button>
          ))}
          {onOpenFaq ? (
            <button
              type="button"
              disabled={disabled || sending}
              className="shrink-0 px-2 text-[11px] font-medium text-primary hover:underline disabled:opacity-50"
              onClick={onOpenFaq}
            >
              FAQ{faqTotal ? ` · ${faqTotal}` : ''}
            </button>
          ) : null}
        </div>
      ) : null}

      {(interim || whisperRecording) && (
        <p className="mb-1.5 px-1 text-xs text-muted-foreground">
          {whisperRecording ? 'Gravando áudio…' : `Ouvindo: ${interim}`}
        </p>
      )}

      <div
        className={cn(
          'flex items-end gap-1 rounded-3xl border border-border/80 bg-card shadow-sm',
          'focus-within:border-primary/40 focus-within:ring-2 focus-within:ring-primary/15',
          'px-2 py-2'
        )}
      >
        {onPickImage ? (
          <>
            <input
              ref={imageInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (!file) return;
                void onPickImage(file).catch((err: unknown) => {
                  toast.error(err instanceof Error ? err.message : 'Falha ao enviar a foto');
                });
              }}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              title="Enviar foto"
              onClick={() => imageInputRef.current?.click()}
              disabled={disabled || sending || busy}
              className="h-9 w-9 shrink-0 rounded-full text-muted-foreground"
            >
              <ImagePlus className="h-4 w-4" />
            </Button>
          </>
        ) : null}

        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void handleSend();
            }
          }}
          placeholder="Pergunte ao Rique.Ia…"
          rows={1}
          disabled={disabled || sending}
          className={cn(
            'max-h-40 min-h-[2.75rem] flex-1 resize-none bg-transparent px-1 py-2 text-sm leading-relaxed text-foreground',
            'placeholder:text-muted-foreground focus-visible:outline-none disabled:opacity-60'
          )}
        />

        <div className="flex shrink-0 items-center gap-0.5 pb-0.5">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            title={isRecording || whisperRecording ? 'Parar gravação' : 'Falar'}
            onClick={toggleMic}
            disabled={disabled || sending}
            className={cn(
              'h-9 w-9 rounded-full',
              isRecording || whisperRecording ? 'text-red-600' : 'text-muted-foreground'
            )}
          >
            {isRecording || whisperRecording ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
          </Button>
          {lastAssistantText ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              title="Ouvir última resposta"
              onClick={() => speak(lastAssistantText)}
              disabled={disabled}
              className="hidden h-9 w-9 rounded-full text-muted-foreground sm:inline-flex"
            >
              <Volume2 className="h-4 w-4" />
            </Button>
          ) : null}
          {busy && onStop ? (
            <Button
              type="button"
              variant="destructive"
              size="sm"
              title="Cancelar requisição (Esc)"
              onClick={onStop}
              className="h-9 gap-1.5 rounded-full px-3"
              aria-label="Cancelar requisição ao Rique"
            >
              <Square className="h-3 w-3 fill-current" aria-hidden />
              <span className="text-xs font-medium">Cancelar</span>
            </Button>
          ) : (
            <Button
              type="button"
              size="icon"
              onClick={() => void handleSend()}
              disabled={disabled || sending || !text.trim()}
              className="h-9 w-9 rounded-full"
              title="Enviar"
            >
              <Send className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
      <p className="mt-2 px-1 text-center text-[10px] text-muted-foreground/80">
        {busy ? (
          <>
            Consulta em andamento
            {onStop ? (
              <>
                {' · '}
                <button
                  type="button"
                  className="inline-flex items-center gap-1 font-medium text-destructive underline-offset-2 hover:underline"
                  onClick={onStop}
                >
                  <XCircle className="h-3 w-3" aria-hidden />
                  Cancelar (Esc)
                </button>
              </>
            ) : null}
          </>
        ) : (
          <>
            Enter envia · Shift+Enter quebra linha
            {emptyState && onOpenFaq ? (
              <>
                {' · '}
                <button type="button" className="underline-offset-2 hover:underline" onClick={onOpenFaq}>
                  ver FAQ{faqTotal ? ` (${faqTotal})` : ''}
                </button>
              </>
            ) : null}
          </>
        )}
      </p>
    </div>
  );
};
